// Vuex module — POS Promotion Release / Versioning.
//
// fetchAll() sudah LIVE → GET unionweb/promotion/release/overview
//   (activePromos, calendar, releases, draft, next_version dari pos_pre_* nyata).
// fetchSyncStores() sudah LIVE → GET .../release/sync-status
//   (pos_store_sync_status; POS menulis reported_version/reported_at langsung ke tabel).
//
// Design ref: docs/project/pos-promotion-versioning.md
import $axios from "../../api";

const PREFIX = "unionweb/promotion/release";

const EMPTY_CALENDAR = { month_label: "", days: 30, next_version: null, today: 0, groups: [] };

// Request calendar-group yang sedang berjalan, key `${asOf}|${type}` — cegah tembakan
// ganda saat search meng-emit load-all per ketikan (lihat fetchCalendarGroup).
const INFLIGHT_CAL_GROUPS = new Set();
// ---------------------------------------------------------------------------

const state = () => ({
  loading: false,
  national: [],
  calendar: { ...EMPTY_CALENDAR },
  publish: { effective_from: null, scheduled: false, can_publish: false, next_version: null, count: 0 },
  sync: { active_national: null, total_stores: 0, behind: 0, synced: 0 },
  syncStores: [],
  scope: "national",
});

// Tidak ada `unchanged` di sini, dan itu memang disengaja: guard "tidak ada
// perubahan dibanding versi aktif" sekarang hanya hidup di server, di cut(), yang
// menjawab 422 `no_changes`. Menghitungnya di jalur baca dulu memakan 3,9 dtk per
// page load. Lihat publishContext() di PromotionReleaseController.
const EMPTY_PUBLISH = { effective_from: null, scheduled: false, can_publish: false, next_version: null, count: 0 };

const mutations = {
  SET_LOADING(s, v) { s.loading = v; },
  SET_NATIONAL(s, v) { s.national = v || []; },
  SET_CALENDAR(s, v) { s.calendar = v || { ...EMPTY_CALENDAR }; },
  SET_PUBLISH(s, v) { s.publish = v || { ...EMPTY_PUBLISH }; },
  SET_SYNC(s, v) { s.sync = v || { active_national: null, total_stores: 0, behind: 0, synced: 0 }; },
  SET_SYNC_STORES(s, v) { s.syncStores = v || []; },
  SET_SCOPE(s, v) { s.scope = v; },
  // LAZY: sisipkan rows satu tipe (dari calendar-group) ke header ringan yg sudah ada.
  // Merge ke groups nasional + groups per-toko (custom). Reassign calendar utk reaktivitas Vue2.
  //
  // Status muat dicatat di calendar._loadedTypes, BUKAN sebagai flag di objek grup:
  // tipe yg hanya ada di toko tidak punya entri di calendar.groups, jadi flag per-grup
  // tak akan pernah tersimpan → tiap toggle re-fetch. Karena _loadedTypes ikut di dalam
  // calendar, SET_CALENDAR (ganti bulan/scope) otomatis mereset-nya.
  MERGE_CAL_GROUP(s, d) {
    if (!d || !d.type) return;
    const groups = (s.calendar.groups || []).map((g) =>
      g.type === d.type ? { ...g, rows: (d.national && d.national.rows) || [] } : g
    );
    const customStores = (s.calendar.customStores || []).map((cs) => {
      const inc = (d.customStores || []).find((x) => x.store_code === cs.store_code);
      if (!inc) return cs;
      const sg = (cs.groups || []).slice();
      (inc.groups || []).forEach((ng) => {
        const i = sg.findIndex((x) => x.type === ng.type);
        if (i >= 0) sg.splice(i, 1, ng); else sg.push(ng);
      });
      return { ...cs, groups: sg };
    });
    s.calendar = {
      ...s.calendar,
      groups,
      customStores,
      _loadedTypes: { ...(s.calendar._loadedTypes || {}), [d.type]: true },
      _failedTypes: { ...(s.calendar._failedTypes || {}), [d.type]: false },
    };
  },
  // Request calendar-group gagal → tandai supaya FE menampilkan "gagal memuat" +
  // tombol coba lagi, bukan spinner yang berputar selamanya.
  MARK_CAL_GROUP_FAILED(s, type) {
    s.calendar = {
      ...s.calendar,
      _failedTypes: { ...(s.calendar._failedTypes || {}), [type]: true },
    };
  },
};

const actions = {
  // LIVE: GET overview -> { national, calendar, publish, sync }
  async fetchAll({ commit, state }) {
    commit("SET_LOADING", true);
    try {
      const res = await $axios.get(`${PREFIX}/overview`, { params: { scope: state.scope } });
      const d = res.data?.data || {};
      commit("SET_NATIONAL", d.national);
      commit("SET_CALENDAR", d.calendar);
      commit("SET_PUBLISH", d.publish);
      commit("SET_SYNC", d.sync);
    } catch (e) {
      // biarkan state default; halaman tetap render (kalender kosong)
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // LIVE: GET calendar?as_of=YYYY-MM-01 -> swap kalender + konteks publish (navigasi bulan)
  async fetchMonth({ commit }, asOf) {
    commit("SET_LOADING", true);
    try {
      const res = await $axios.get(`${PREFIX}/calendar`, { params: { as_of: asOf } });
      const d = res.data?.data || {};
      commit("SET_CALENDAR", d.calendar);
      commit("SET_PUBLISH", d.publish);
    } catch (e) {
      // mis. 403 (tak punya izin release_calendar) → biarkan kalender apa adanya,
      // jangan lempar unhandled rejection.
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // LIVE: GET sync-status per toko (pos_store_sync_status; POS tulis langsung ke tabel).
  async fetchSyncStores({ commit }) {
    commit("SET_LOADING", true);
    try {
      const res = await $axios.get(`${PREFIX}/sync-status`);
      commit("SET_SYNC", res.data?.data?.sync || null);
      commit("SET_SYNC_STORES", res.data?.data?.sync_stores || []);
    } catch (e) {
      // mis. 403 (tak punya izin release_sync_status) → biarkan state kosong,
      // jangan lempar unhandled rejection.
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // LAZY: muat baris & segmen SATU tipe on-demand (saat grup tipe dibuka di kalender).
  // Idempoten — skip kalau tipe sudah dimuat ATAU request-nya masih jalan. asOf = bulan
  // yg sedang dilihat (YYYY-MM-DD). retry=true memaksa muat ulang tipe yg gagal.
  //
  // Penjaga in-flight perlu terpisah dari _loadedTypes: penanda itu baru terpasang saat
  // response tiba, sedangkan search meng-emit load-all TIAP ketikan → tanpa ini satu tipe
  // bisa ditembak berkali-kali sekaligus. Key ikut asOf supaya ganti bulan tidak terhalang.
  async fetchCalendarGroup({ commit, state }, { type, asOf, retry = false }) {
    if (!retry && (state.calendar._loadedTypes || {})[type]) return;

    const key = `${asOf}|${type}`;
    if (INFLIGHT_CAL_GROUPS.has(key)) return;
    INFLIGHT_CAL_GROUPS.add(key);
    try {
      const res = await $axios.get(`${PREFIX}/calendar-group`, { params: { type, as_of: asOf } });
      commit("MERGE_CAL_GROUP", res.data?.data || {});
    } catch (e) {
      // Jangan lempar unhandled rejection, tapi JANGAN diam-diam: tanpa penanda ini
      // grup terlihat "sedang memuat" selamanya karena rows-nya memang kosong.
      commit("MARK_CAL_GROUP_FAILED", type);
    } finally {
      INFLIGHT_CAL_GROUPS.delete(key);
    }
  },

  // Rincian SEMUA promo pada satu produk (termasuk yang tersisih — kalender cuma
  // menggambar pemenang tiap hari, jadi yang tertutup tak kelihatan sama sekali).
  // On-demand: hanya ditembak saat tombolnya diklik, tidak ikut load halaman.
  async fetchSkuPromos(_ctx, { sku, asOf, scope }) {
    const res = await $axios.get(`${PREFIX}/sku-promos`, {
      params: { sku, as_of: asOf, scope: scope || "all" },
    });
    return res.data?.data || null;
  },

  setScope({ commit, dispatch }, scope) {
    commit("SET_SCOPE", scope);
    return dispatch("fetchAll");
  },

  // LIVE: POST cut -> freeze pos_pre_* -> pos_* + tulis baris release
  async cutRelease(_ctx, payload) {
    const res = await $axios.post(`${PREFIX}/cut`, payload || {});
    return res.data?.data;
  },

  // MANUAL: POST reconcile -> jalankan penyelarasan status rilis (fallback cron
  // `promotion:reconcile-releases`). Ketentuan sama dgn CLI.
  async reconcileReleases(_ctx) {
    const res = await $axios.post(`${PREFIX}/reconcile`);
    return res.data?.data;
  },

  // LIVE: POST rollback -> tarik rilis (rolledback); current mundur otomatis ke sebelumnya
  async rollback(_ctx, payload) {
    const { id, notes } = typeof payload === "object" ? payload : { id: payload, notes: null };
    const res = await $axios.post(`${PREFIX}/${id}/rollback`, { notes: notes || null });
    return res.data?.data;
  },

  // LIVE: POST activate -> jadikan versi ini current (versi lebih baru yang sudah berlaku → rolledback)
  async activateRelease(_ctx, payload) {
    const { id, notes } = typeof payload === "object" ? payload : { id: payload, notes: null };
    const res = await $axios.post(`${PREFIX}/${id}/activate`, { notes: notes || null });
    return res.data?.data;
  },

  // LIVE: GET versi aktif sekarang saja (ringan, untuk badge di entry-point)
  async fetchActiveVersion() {
    const res = await $axios.get(`${PREFIX}/active-version`);
    return res.data?.data?.version || null;
  },

  // LIVE: GET kalender snapshot beku versi tertentu (untuk tombol "Lihat versi")
  async fetchReleaseCalendar(_ctx, id) {
    const res = await $axios.get(`${PREFIX}/${id}/calendar`);
    return res.data?.data;
  },

  // LIVE: GET export-sap -> unduh 12 tabel agenda SAP versi ini (zip berisi 12 .xlsx).
  // Response berupa blob; nama file diambil dari header Content-Disposition.
  // payload: id | { id, listNum } — listNum menimpa kolom ListNum (OSPP & SPP1).
  async exportSap(_ctx, payload) {
    const { id, listNum } = typeof payload === "object" ? payload : { id: payload, listNum: null };
    const params = {};
    if (listNum !== null && listNum !== undefined && listNum !== "") params.list_num = listNum;
    const res = await $axios.get(`${PREFIX}/${id}/export-sap`, { params, responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return { blob: res.data, filename: m ? decodeURIComponent(m[1]) : `sap-promo-${id}.zip` };
  },
};

const getters = {
  activeNational: (s) => s.national.find((r) => r.status === "active") || null,
  hasDraft: (s) => (s.publish.count || 0) > 0,
  calendarRowCount: (s) => (s.calendar.groups || []).reduce((n, g) => n + g.rows.length, 0),
};

export default { namespaced: true, state, mutations, actions, getters };
